import { axiosInstance } from "./apiClient";

export interface ExtractedItem {
  code: string;
  description: string;
  udm: string;
  qta: number;
}

export interface ExtractionPreviewResult {
  temp_id: string;
  total_items: number;
  headers_found: number;
  multirow_groups: number;
  notes_applied: number;
  rows_skipped: number;
  items: ExtractedItem[];
}

/** Timeout override (ms) for the Excel preview call. */
const EXCEL_TIMEOUT_MS = 120_000; // 2 min

/** A page-window that failed extraction after exhausting all retries. */
export interface PdfWindowFailure {
  window_index: number;
  pages: number[];
  error: string;
  attempts: number;
}

/**
 * Report returned by the PDF extraction pipeline once a job is done — see
 * backend `pdf_mp.schema.ExtractionReport.to_dict()`.
 */
export interface PdfExtractionReport {
  total_pages: number;
  total_windows: number;
  windows_ok: number;
  windows_failed: number;
  failures: PdfWindowFailure[];
  audit_passes: number;
  audit_expected_count: number | null;
  audit_found_count: number | null;
  audit_missing: string[];
  audit_duplicated: string[];
  breve_compacted: number;
  breve_still_over_limit: number;
  elapsed_seconds: number;
  total_items: number;
  total_chapters: number;
  /** Sezioni riusate da un'esecuzione precedente (ripresa). Assente nei report vecchi. */
  windows_resumed?: number;
  /** Assente nei report generati prima dell'introduzione degli avvisi. */
  review_alerts?: PdfReviewAlert[];
}

/** Riga su cui una correzione automatica ha fatto un'assunzione da verificare. */
export interface PdfReviewAlert {
  pagina: number;
  codice: string;
  descrizione: string;
  motivo: string;
}

/** Background PDF -> IMPORT Excel job status, as polled from the server. */
export type PdfJobStatus =
  | "queued"
  | "rendering"
  | "extracting"
  | "auditing"
  | "compacting"
  | "writing"
  | "done"
  | "failed"
  | "cancelled";

export interface PdfJob {
  job_id: string;
  status: PdfJobStatus;
  progress_pct: number;
  stage_label: string;
  filename: string;
  completed_units: number;
  total_units: number;
  error: string | null;
  report: PdfExtractionReport | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  can_download: boolean;
  /** Pagine del PDF, lette al caricamento. */
  total_pages: number | null;
  /** Durata totale stimata (secondi), calcolata al caricamento. */
  estimated_seconds: number | null;
  /** Secondi residui stimati (solo per job in corso). */
  estimated_remaining_seconds: number | null;
  /** Sezioni già estratte in un'esecuzione precedente e riusate. */
  resumed_units: number;
  /** True se la conversione può essere ripresa senza ricaricare il PDF. */
  can_resume: boolean;
}

/** Shape of the 409 response detail when a job is already running. */
export interface PdfJobConflict {
  message: string;
  job: PdfJob;
}

export const extractorApi = {
  /**
   * Upload an Excel tender file for validation and extraction.
   * Returns extraction stats + items + a temp_id for the debug-Excel download.
   */
  preview: async (file: File): Promise<ExtractionPreviewResult> => {
    const form = new FormData();
    form.append("file", file);
    const res = await axiosInstance.post<ExtractionPreviewResult>(
      "/extractor/preview",
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: EXCEL_TIMEOUT_MS,
      }
    );
    return res.data;
  },

  /**
   * Download the debug-coloured Excel produced by a previous preview() call.
   * Triggers a browser file-save automatically.
   */
  downloadDebug: async (tempId: string, suggestedName?: string): Promise<void> => {
    const res = await axiosInstance.get(`/extractor/debug/${tempId}`, {
      responseType: "blob",
      timeout: 30_000,
    });
    const url = window.URL.createObjectURL(res.data as Blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = suggestedName ?? `debug_${tempId.slice(0, 8)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Start a background PDF -> IMPORT Excel conversion job. Returns
   * immediately with the job status (queued). Throws an axios error with
   * status 409 and `response.data.detail` shaped as PdfJobConflict if the
   * user already has one running.
   */
  startPdfJob: async (file: File): Promise<PdfJob> => {
    const form = new FormData();
    form.append("file", file);
    const res = await axiosInstance.post<PdfJob>("/extractor/pdf-jobs", form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 30_000,
    });
    return res.data;
  },

  /** Active job for the current user, or their most recent one, or null. */
  getCurrentPdfJob: async (): Promise<PdfJob | null> => {
    const res = await axiosInstance.get<PdfJob | null>("/extractor/pdf-jobs/current", {
      timeout: 15_000,
    });
    return res.data ?? null;
  },

  /** Poll a specific job's status + progress. */
  getPdfJob: async (jobId: string): Promise<PdfJob> => {
    const res = await axiosInstance.get<PdfJob>(`/extractor/pdf-jobs/${jobId}`, {
      timeout: 15_000,
    });
    return res.data;
  },

  /** Recent job history for the current user (most recent first). */
  listPdfJobs: async (): Promise<PdfJob[]> => {
    const res = await axiosInstance.get<PdfJob[]>("/extractor/pdf-jobs", {
      timeout: 15_000,
    });
    return res.data;
  },

  /** Permanently delete a finished/failed/cancelled job (and its file, if any) from the history. */
  deletePdfJob: async (jobId: string): Promise<void> => {
    await axiosInstance.delete(`/extractor/pdf-jobs/${jobId}`, { timeout: 15_000 });
  },

  /** Request cooperative cancellation of a running PDF conversion job. */
  cancelPdfJob: async (jobId: string): Promise<PdfJob> => {
    const res = await axiosInstance.post<PdfJob>(`/extractor/pdf-jobs/${jobId}/cancel`, null, {
      timeout: 15_000,
    });
    return res.data;
  },

  /**
   * Resume a failed job (or retry only the failed sections of a finished one)
   * from the pages already analysed, without re-uploading the PDF.
   */
  resumePdfJob: async (jobId: string): Promise<PdfJob> => {
    const res = await axiosInstance.post<PdfJob>(`/extractor/pdf-jobs/${jobId}/resume`, null, {
      timeout: 15_000,
    });
    return res.data;
  },

  /** Download the finished Excel for a completed job, triggering a browser save. */
  downloadPdfJobResult: async (jobId: string, suggestedName?: string): Promise<void> => {
    const res = await axiosInstance.get(`/extractor/pdf-jobs/${jobId}/download`, {
      responseType: "blob",
      timeout: 60_000,
    });
    const blob = res.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const disposition: string = (res.headers as Record<string, string>)["content-disposition"] ?? "";
    const match = disposition.match(/filename="?([^";\n]+)"?/i);
    a.download = match ? match[1] : suggestedName ?? "computo_IMPORT.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};
